/**
 * Social sign-in helpers (Google + WeChat).
 *
 * The backend owns the redirect choreography (see backend/src/routes/socialAuth.ts):
 * we send the browser to `/auth/social/:provider/authorize` with the origin we
 * must come back to and the path to resume after login. The provider redirects
 * to the backend's canonical callback, which hands the browser back to
 * `<origin>/social-callback?ssoToken=…` (the same landing page used by
 * cross-domain SSO), where the token is exchanged for a real session.
 *
 * No access token, client id or secret ever touches this bundle — those live
 * only on the server.
 */
import { API_URL } from '../config/env';

const BASE = `${API_URL.replace(/\/$/, '')}/auth/social`;

/** Which provider buttons should be rendered. */
export async function fetchSocialProviders() {
  const res = await fetch(`${BASE}/providers`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (!res.ok) return { google: { enabled: false }, wechat: { enabled: false, qr: false } };
  const data = await res.json().catch(() => ({}));
  return {
    google: { enabled: Boolean(data?.google?.enabled) },
    wechat: {
      enabled: Boolean(data?.wechat?.enabled),
      qr: Boolean(data?.wechat?.qr),
      mode: data?.wechat?.mode,
    },
  };
}

/**
 * URL that starts the provider redirect for this origin.
 * @param {'google'|'wechat'} provider
 * @param {{ next?: string, origin?: string }} opts
 */
export function socialAuthorizeUrl(provider, opts = {}) {
  const origin =
    opts.origin ||
    (typeof window !== 'undefined' ? window.location.origin : 'https://www.mgv.gallery');
  const next = opts.next || (typeof window !== 'undefined' ? window.location.pathname : '/account');
  const params = new URLSearchParams({ origin, next });
  return `${BASE}/${provider}/authorize?${params.toString()}`;
}

/** Open the provider redirect (full navigation — required by Google/WeChat). */
export function startSocialLogin(provider, opts = {}) {
  window.location.assign(socialAuthorizeUrl(provider, opts));
}

/** WeChat QR can be embedded instead of navigating away. */
export async function fetchWechatQrUrl({ next } = {}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
  const params = new URLSearchParams({ display: 'iframe' });
  if (origin) params.set('origin', origin);
  if (next) params.set('next', next);
  const res = await fetch(`${BASE}/wechat/authorize?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return data?.authorizeUrl || null;
}

/** Human-readable message for the `?socialError=` the callback may append. */
export function socialErrorMessage(reason) {
  switch (reason) {
    case 'no_code':
      return 'Sign-in was cancelled before it completed.';
    case 'state':
      return 'Sign-in link expired. Please try again.';
    case 'exchange':
      return 'We could not verify your account with the provider. Please try again.';
    case 'profile':
      return 'Your provider profile was missing required information.';
    case 'wechat_not_configured':
      return 'WeChat sign-in is not enabled yet.';
    default:
      return 'Social sign-in failed. Please try again.';
  }
}

export default fetchSocialProviders;
